import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Briefcase, DollarSign, Settings, LogOut,
  TrendingUp, Users, Star, Clock, CheckCircle2, AlertCircle,
  MoreVertical, Calendar, ChevronRight, Plus, X, Mail, Phone, MapPin,
  Building, Globe, Award, Languages, ShoppingBag, Package, Eye, EyeOff,
  Edit3, Trash2, Loader2, Search
} from 'lucide-react';
import { ServiceEditor } from '../digitalservices/ServiceEditor';
import { ServicesService, ServiceStatus } from '../../services/servicesService';
import { ProviderSettings } from './ProviderSettings';
import EmailList from '../email/EmailList';
import EmailTemplateManager from '../email/EmailTemplateManager';
import EmailComposer from '../email/EmailComposer';

export const ProviderDashboard = ({ provider: initialProvider, onLogout, onSwitchToBuying, onServiceSaved }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [provider, setProvider] = useState(initialProvider);

  const handleProviderUpdate = (updatedProvider) => {
    setProvider(updatedProvider);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview provider={provider} />;
      case 'services':
        return <DashboardServices providerId={provider.id} />;
      case 'projects':
        return <DashboardProjects />;
      case 'email':
        return <DashboardEmail />;
      case 'earnings':
        return <DashboardEarnings />;
      case 'settings':
        return <ProviderSettings provider={provider} onSave={handleProviderUpdate} />;
      default:
        return <DashboardOverview provider={provider} />;
    }
  };

  return (
    <div className="max-h-screen bg-slate-50 flex flex-col md:flex-row font-sans overflow-hidden animate-fade-in">
      {/* Sidebar */}
      <aside className="w-full md:w-72 bg-gradient-to-b from-teal-100 via-purple-100 to-rose-100 flex-shrink-0 flex flex-col md:rounded-r-[2.5rem] shadow-2xl z-10 border-r border-white/40">
        <div className="p-8 border-b border-white/20 backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <img src={provider.avatarUrl} alt={provider.name} className="w-12 h-12 rounded-full border-2 border-white shadow-sm object-cover" />
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-sm truncate">{provider.name}</h3>
              <p className="text-xs text-indigo-700 font-bold bg-white/50 inline-block px-2 py-0.5 rounded-full mt-1 backdrop-blur-sm">Provider Account</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-6 space-y-3">
          <NavButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={LayoutDashboard} label="Overview" />
          <NavButton active={activeTab === 'services'} onClick={() => setActiveTab('services')} icon={Package} label="My Services" />
          <NavButton active={activeTab === 'projects'} onClick={() => setActiveTab('projects')} icon={Briefcase} label="Projects" />
          <NavButton active={activeTab === 'email'} onClick={() => setActiveTab('email')} icon={Mail} label="Email" />
          <NavButton active={activeTab === 'earnings'} onClick={() => setActiveTab('earnings')} icon={DollarSign} label="Earnings" />
          <NavButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings} label="Settings" />
        </nav>

        <div className="p-6 border-t border-white/20 backdrop-blur-sm flex flex-col gap-2">
          <button onClick={onSwitchToBuying} className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-bold text-slate-600 hover:text-indigo-600 hover:bg-white/50 rounded-2xl transition-all">
            <ShoppingBag className="w-5 h-5" />
            Switch to Buying
          </button>
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-5 py-3.5 text-sm font-bold text-slate-600 hover:text-red-600 hover:bg-white/50 rounded-2xl transition-all">
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto h-screen p-4 md:p-10">
        <div className="max-w-5xl mx-auto">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

const NavButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-bold rounded-2xl transition-all duration-200 ${active
      ? 'bg-white text-indigo-600 shadow-lg shadow-indigo-900/10 scale-105'
      : 'text-slate-600 hover:bg-white/40 hover:text-indigo-900'
      }`}
  >
    <Icon className="w-5 h-5" />
    {label}
  </button>
);

// --- Services Management Tab ---
const DashboardServices = ({ providerId }) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const data = await ServicesService.getByProviderId(providerId);
      setServices(data);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadServices();
  }, [providerId]);

  const handleSave = (savedService) => {
    if (editingService) {
      setServices(prev => prev.map(s => s.id === savedService.id ? savedService : s));
    } else {
      setServices(prev => [savedService, ...prev]);
    }
    setEditingService(null);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setEditorOpen(true);
  };

  const handleToggleStatus = async (service) => {
    setActionLoading(service.id);
    try {
      const newStatus = service.status === ServiceStatus.PUBLISHED
        ? ServiceStatus.DRAFT
        : ServiceStatus.PUBLISHED;
      await ServicesService.update(service.id, { status: newStatus });
      setServices(prev => prev.map(s =>
        s.id === service.id ? { ...s, status: newStatus } : s
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    setActionLoading(serviceId);
    try {
      await ServicesService.delete(serviceId);
      setServices(prev => prev.filter(s => s.id !== serviceId));
    } catch (error) {
      console.error('Error deleting service:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredServices = services.filter(service => {
    const matchesFilter = filter === 'all' || service.status === filter;
    const matchesSearch = !searchQuery ||
      service.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total: services.length,
    published: services.filter(s => s.status === ServiceStatus.PUBLISHED).length,
    draft: services.filter(s => s.status === ServiceStatus.DRAFT).length,
  };

  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">My Services</h1>
          <p className="text-slate-500 mt-1">Manage your service offerings</p>
        </div>
        <button
          onClick={() => { setEditingService(null); setEditorOpen(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
        >
          <Plus className="w-5 h-5" />
          Add Service
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-slate-500 text-sm">Total</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-green-600 text-sm flex items-center gap-1"><Eye className="w-4 h-4" /> Published</p>
          <p className="text-2xl font-bold text-slate-900">{stats.published}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
          <p className="text-amber-600 text-sm flex items-center gap-1"><EyeOff className="w-4 h-4" /> Drafts</p>
          <p className="text-2xl font-bold text-slate-900">{stats.draft}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <div className="flex gap-2">
          {['all', ServiceStatus.PUBLISHED, ServiceStatus.DRAFT].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${filter === f
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
            >
              {f === 'all' ? 'All' : f === ServiceStatus.PUBLISHED ? 'Published' : 'Drafts'}
            </button>
          ))}
        </div>
      </div>

      {/* Services List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">No services found</h3>
          <p className="text-slate-500 mb-6">
            {services.length === 0
              ? "Create your first service to start selling"
              : "Try adjusting your filters"}
          </p>
          {services.length === 0 && (
            <button
              onClick={() => { setEditingService(null); setEditorOpen(true); }}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
            >
              Create Service
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50/50 text-slate-500">
                <tr>
                  <th className="px-6 py-4 font-medium">Service</th>
                  <th className="px-6 py-4 font-medium hidden md:table-cell">Category</th>
                  <th className="px-6 py-4 font-medium">Price</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredServices.map(service => (
                  <tr key={service.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={service.imageUrl}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover bg-slate-100"
                        />
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate max-w-[200px]">{service.title}</p>
                          <p className="text-xs text-slate-500 md:hidden">{service.category}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 hidden md:table-cell">{service.category}</td>
                    <td className="px-6 py-4 font-bold text-slate-900">R{service.priceStart}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${service.status === ServiceStatus.PUBLISHED
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                        }`}>
                        {service.status === ServiceStatus.PUBLISHED ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleStatus(service)}
                          disabled={actionLoading === service.id}
                          className={`p-2 rounded-lg transition-colors ${service.status === ServiceStatus.PUBLISHED
                            ? 'text-amber-600 hover:bg-amber-50'
                            : 'text-green-600 hover:bg-green-50'
                            }`}
                          title={service.status === ServiceStatus.PUBLISHED ? 'Unpublish' : 'Publish'}
                        >
                          {actionLoading === service.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : service.status === ServiceStatus.PUBLISHED ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(service)}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          disabled={actionLoading === service.id}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Service Editor Modal */}
      <ServiceEditor
        isOpen={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingService(null); }}
        providerId={providerId}
        service={editingService}
        onSave={handleSave}
      />
    </div>
  );
};

// --- Overview Tab ---
const DashboardOverview = ({ provider }) => {
  return (
    <div className="space-y-8 animate-fade-in-up">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back, {provider.name.split(' ')[0]}</p>
        </div>
        <span className="text-sm font-medium text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100 hidden sm:block">
          {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Earnings" value="R12,450" change="+12%" icon={DollarSign} color="bg-indigo-600" />
        <StatCard title="Active Jobs" value="3" subtext="2 due this week" icon={Briefcase} color="bg-blue-500" />
        <StatCard title="Rating" value={provider.rating.toString()} subtext={`${provider.reviewCount} reviews`} icon={Star} color="bg-yellow-400" />
        <StatCard title="Profile Views" value="1.2k" change="+5%" icon={Users} color="bg-purple-500" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
        <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-slate-900 mb-2">Manage Your Services</h3>
        <p className="text-slate-500 mb-4">Add, edit, and publish services from the My Services tab</p>
      </div>
    </div>
  );
};

// --- Email Tab ---
const DashboardEmail = () => {
  const [emailView, setEmailView] = useState('list'); // list, templates, compose
  const [composerData, setComposerData] = useState(null);

  const handleCompose = (data = null, mode = 'compose') => {
    setComposerData({
      emailData: data,
      mode: mode,
      templateId: null
    });
    setEmailView('compose');
  };

  const handleUseTemplate = (templateId) => {
    setComposerData({
      emailData: null,
      mode: 'compose',
      templateId: templateId
    });
    setEmailView('compose');
  };

  const handleSent = () => {
    setEmailView('list');
    setComposerData(null);
  };

  return (
    <div className="space-y-6 animate-fade-in-up h-full flex flex-col">
      {/* Email Navigation */}
      {emailView !== 'compose' && (
        <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-fit mb-4">
          <button
            onClick={() => setEmailView('list')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${emailView === 'list'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-50'
              }`}
          >
            Inbox & History
          </button>
          <button
            onClick={() => setEmailView('templates')}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${emailView === 'templates'
              ? 'bg-indigo-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-slate-50'
              }`}
          >
            Templates
          </button>
        </div>
      )}

      {/* Email Views */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex-1 p-6 overflow-hidden flex flex-col">
        {emailView === 'list' && (
          <EmailList onCompose={handleCompose} />
        )}

        {emailView === 'templates' && (
          <EmailTemplateManager onUseTemplate={handleUseTemplate} />
        )}

        {emailView === 'compose' && (
          <div className="h-full flex flex-col">
            <div className="mb-4">
              <button
                onClick={() => setEmailView('list')}
                className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1"
              >
                ← Back to Inbox
              </button>
            </div>
            <EmailComposer
              isFullPage={false}
              onClose={() => setEmailView('list')}
              onSent={handleSent}
              emailData={composerData?.emailData}
              mode={composerData?.mode}
              templateId={composerData?.templateId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// --- Projects Tab ---
const DashboardProjects = () => {
  return (
    <div className="space-y-8 animate-fade-in-up">
      <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Projects</h1>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
        <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-slate-900 mb-2">No Active Projects</h3>
        <p className="text-slate-500">Projects will appear here when clients book your services</p>
      </div>
    </div>
  );
};

// --- Earnings Tab ---
const DashboardEarnings = () => {
  return (
    <div className="space-y-8 animate-fade-in-up">
      <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Earnings</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-indigo-600 rounded-2xl p-8 text-white shadow-xl shadow-indigo-200">
          <p className="text-indigo-100 text-sm font-medium mb-2">Available for Withdrawal</p>
          <h2 className="text-4xl font-bold mb-6">R0.00</h2>
          <button className="w-full bg-white text-indigo-600 py-3 rounded-xl font-bold hover:bg-indigo-50 transition-colors shadow-sm">
            Withdraw Funds
          </button>
        </div>
        <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm flex flex-col justify-center">
          <p className="text-slate-500 text-sm font-medium mb-2">Pending Clearance</p>
          <h2 className="text-4xl font-bold text-slate-900 mb-2">R0.00</h2>
          <p className="text-xs text-slate-400">Funds clear 14 days after job completion</p>
        </div>
        <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm flex flex-col justify-center">
          <p className="text-slate-500 text-sm font-medium mb-2">Total Earned (All Time)</p>
          <h2 className="text-4xl font-bold text-slate-900 mb-2">R0.00</h2>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const StatCard = ({ title, value, change, subtext, icon: Icon, color }) => (
  <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-start justify-between hover:shadow-md transition-shadow duration-300">
    <div>
      <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-slate-900">{value}</h3>
      {change && <p className="text-green-600 text-xs font-bold mt-2 bg-green-50 inline-block px-2 py-0.5 rounded-md">{change} from last month</p>}
      {subtext && <p className="text-slate-400 text-xs mt-2">{subtext}</p>}
    </div>
    <div className={`p-3.5 rounded-xl text-white shadow-lg ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
  </div>
);